import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { validarArchivo } from "@/lib/adjuntos";
import { api, type Adjunto, type Categoria, type Cotizacion, type CotizacionDetalle, type EscaneoCotizacion, type EscaneoDisponible, type Proveedor } from "@/lib/api";
import {
  aPayloadCotizacion,
  armarArbol,
  buscarCotizaciones,
  compararCotizaciones,
  estaVencida,
  formCotizacion,
  formDesdeEscaneo,
  mensajeDuplicado,
  lineasDesdeEscaneo,
  mensajeBorrado,
  opcionesEstadoForm,
  opcionesProveedorCotizacion,
  opcionesSubcategoria,
  queryCotizaciones,
  TODOS,
  validarCotizacion,
  type ErroresCotizacion,
  type EtapaRama,
  type FormCotizacion,
  type PayloadCotizacion,
} from "@/lib/cotizaciones";
import { hoyISO } from "@/lib/format";
import { cuotasDesdeEscaneo } from "@/lib/formaDePago";
import { usePasosManual } from "./usePasosManual";
import { useEdicionCategorias } from "./useEdicionCategorias";

type Dialogo =
  | { tipo: "form"; cotizacion: Cotizacion | null }
  | { tipo: "borrar"; cotizacion: Cotizacion }
  | { tipo: "adjuntos"; cotizacion: Cotizacion }
  | { tipo: "duplicado"; archivo: File; escaneo: EscaneoCotizacion }
  | null;

const SIN_ERRORES: ErroresCotizacion = {};

/** El presupuesto escaneado: el archivo se adjunta al guardar el alta; `escaneo` trae el nombre leído del proveedor. */
interface Escaneado {
  archivo: File;
  escaneo: EscaneoCotizacion;
}

/** Lógica de Presupuestos recibidos (sin JSX). Admin carga y edita; contador sólo mira (el backend lo hace cumplir).
 * Se ven en un árbol plegable categoría → subcategoría → presupuestos (Mati, 2026-10-30), con los de antes sin
 * subcategoría aparte para ubicarlos; el admin edita el árbol con `useEdicionCategorias`. */
export function useCotizacionesTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/cotizaciones`;
  const esAdmin = membresia.rol === "admin";
  const hoy = hoyISO();

  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const proveedorParam = params.get("proveedor");
  const proveedorId = proveedorParam && /^\d+$/.test(proveedorParam) ? proveedorParam : null;
  const [estado, setEstado] = useState(TODOS);
  const [texto, setTexto] = useState("");
  // Plegado del árbol por clave ("c<id>" categoría, "s<id>" subcategoría, "sin", "e<id>" en edición); sin clave:
  // cerrado, salvo con filtros (se abre lo que coincide).
  const [abiertas, setAbiertas] = useState<Record<string, boolean>>({});

  const listado = useApiResource<Cotizacion[]>(`${base}${queryCotizaciones({ estado, proveedorId })}`);
  const proveedores = useApiResource<Proveedor[]>(`/api/fideicomisos/${fid}/proveedores?archivados=true`);
  const categorias = useApiResource<Categoria[]>(`/api/fideicomisos/${fid}/presupuesto/categorias`);
  const edicion = useEdicionCategorias({
    alCambiar: () => {
      categorias.recargar();
      listado.recargar(); // cambian los números de los presupuestos
    },
  });
  const escaneo = useApiResource<EscaneoDisponible>(esAdmin ? `${base}/escanear` : null);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormCotizacion>(() => formCotizacion());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [escaneado, setEscaneado] = useState<Escaneado | null>(null);
  const [guardarLineas, setGuardarLineas] = useState(true);
  const [guardarCuotas, setGuardarCuotas] = useState(true);
  /** Se abrió Editar desde la ficha (`?editar=<id>`): al cerrar, se vuelve a ella. */
  const [volverAFicha, setVolverAFicha] = useState<number | null>(null);
  const [errorArchivoEscaneo, setErrorArchivoEscaneo] = useState<string | null>(null);

  const conAdjuntos = (dialogo?.tipo === "form" || dialogo?.tipo === "adjuntos") && dialogo.cotizacion ? dialogo.cotizacion : null;
  const detalle = useApiResource<CotizacionDetalle>(conAdjuntos ? `${base}/${conAdjuntos.id}` : null);
  // useApiResource no limpia `data` al cambiar de ruta: no mostrar los adjuntos de otro presupuesto mientras carga.
  const adjuntos = conAdjuntos && detalle.data?.id === conAdjuntos.id ? detalle.data.adjuntos : [];

  const accionGuardar = useAsyncAction((id: number | null, datos: PayloadCotizacion) =>
    id === null ? api.post<Cotizacion>(base, datos) : api.patch<Cotizacion>(`${base}/${id}`, datos),
  );
  // Alta rápida desde el form: sólo el nombre; el resto de la ficha se completa después en Proveedores.
  const accionCrearProveedor = useAsyncAction((nombre: string, cuit: string | null = null) => api.post<Proveedor>(`/api/fideicomisos/${fid}/proveedores`, cuit ? { nombre, cuit } : { nombre }));
  const [proveedorCreado, setProveedorCreado] = useState<Proveedor | null>(null);
  const accionBorrar = useAsyncAction((id: number) => api.delete<{ resultado: "borrado" }>(`${base}/${id}`).then(() => true));
  const accionSubir = useAsyncAction((id: number, archivo: File) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    return api.upload<Adjunto>(`${base}/${id}/adjuntos`, datos);
  });
  const accionEscanear = useAsyncAction((archivo: File) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    return api.upload<EscaneoCotizacion>(`${base}/escanear`, datos);
  });
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  /** El proveedor que figura en el presupuesto escaneado y no está en la agenda: se agrega solo al guardar (Mati,
   * 2026-10-29). Null si se encontró, si ya se eligió otro o si no es un alta escaneada. */
  const proveedorLeidoNuevo =
    dialogo?.tipo === "form" && dialogo.cotizacion === null && escaneado && !form.proveedor_id ? escaneado.escaneo.proveedor_nombre : null;
  const errores = validarCotizacion(form, proveedorLeidoNuevo !== null);
  const cerrar = () => {
    setDialogo(null);
    if (volverAFicha !== null) navigate(`/cotizaciones/${volverAFicha}`);
  };

  const abrirForm = (cotizacion: Cotizacion | null, nuevo?: FormCotizacion) => {
    setForm(nuevo ?? formCotizacion(cotizacion ?? undefined, hoy));
    if (!nuevo) setEscaneado(null);
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    accionCrearProveedor.limpiarError();
    accionSubir.limpiarError();
    accionBorrarAdjunto.limpiarError();
    setDialogo({ tipo: "form", cotizacion });
  };

  const cambiar = <K extends keyof FormCotizacion>(campo: K, valor: FormCotizacion[K]) => setForm((f) => ({ ...f, [campo]: valor }));

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
    let proveedorId = form.proveedor_id;
    if (proveedorLeidoNuevo && escaneado) {
      const nuevo = await accionCrearProveedor.run(proveedorLeidoNuevo, escaneado.escaneo.proveedor_cuit);
      if (!nuevo) return;
      setProveedorCreado(nuevo);
      proveedorId = String(nuevo.id);
      cambiar("proveedor_id", proveedorId); // si falla el guardado, el reintento ya no lo vuelve a crear
      proveedores.recargar();
    }
    const payload = aPayloadCotizacion({ ...form, proveedor_id: proveedorId });
    const leido = dialogo.cotizacion === null && escaneado ? escaneado.escaneo : null;
    const guardada = await accionGuardar.run(dialogo.cotizacion?.id ?? null, {
      ...payload,
      ...(leido && guardarLineas && leido.lineas.length > 0 ? { lineas: lineasDesdeEscaneo(leido.lineas) } : {}),
      ...(leido && guardarCuotas && leido.cuotas.length > 0 ? { cuotas: cuotasDesdeEscaneo(leido.cuotas) } : {}),
    });
    if (!guardada) return;
    listado.recargar();
    if (dialogo.cotizacion === null && escaneado) {
      // El presupuesto escaneado queda como su adjunto. Si falla, el form pasa a Editar con el error para reintentar.
      const subido = await accionSubir.run(guardada.id, escaneado.archivo);
      setEscaneado(null);
      if (!subido) {
        setDialogo({ tipo: "form", cotizacion: guardada });
        return;
      }
    }
    if (dialogo.cotizacion === null) {
      // Uno nuevo: se abre su ficha, con el PDF, la forma de pago y Aceptar (Mati, 2026-10-29).
      setDialogo(null);
      navigate(`/cotizaciones/${guardada.id}`);
      return;
    }
    cerrar();
  };

  const escanear = async (archivo: File) => {
    accionEscanear.limpiarError();
    const invalido = validarArchivo(archivo);
    setErrorArchivoEscaneo(invalido);
    if (invalido) return;
    const leido = await accionEscanear.run(archivo);
    if (!leido) return;
    // Parece uno ya cargado (Mati, 2026-10-29: escaneó dos veces el mismo y quedó duplicado): se pregunta antes del alta.
    if (leido.duplicado) setDialogo({ tipo: "duplicado", archivo, escaneo: leido });
    else abrirAltaEscaneada(archivo, leido);
  };

  const abrirAltaEscaneada = (archivo: File, leido: EscaneoCotizacion) => {
    abrirForm(null, formDesdeEscaneo(leido, hoy));
    setEscaneado({ archivo, escaneo: leido });
    setGuardarLineas(true);
    setGuardarCuotas(true);
  };

  const crearProveedor = async (nombre: string, cuit: string | null = null) => {
    const nuevo = await accionCrearProveedor.run(nombre, cuit);
    if (!nuevo) return;
    setProveedorCreado(nuevo);
    cambiar("proveedor_id", String(nuevo.id));
    proveedores.recargar();
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.cotizacion.id)) {
      cerrar();
      listado.recargar();
    }
  };

  const subirAdjunto = async (archivo: File) => {
    if (!conAdjuntos) return;
    accionBorrarAdjunto.limpiarError();
    if (await accionSubir.run(conAdjuntos.id, archivo)) {
      detalle.recargar();
      listado.recargar(); // cambia la cantidad de adjuntos
    }
  };

  const borrarAdjunto = async (a: Adjunto) => {
    accionSubir.limpiarError();
    if (await accionBorrarAdjunto.run(a.id)) {
      detalle.recargar();
      listado.recargar();
    }
  };

  const todas = listado.data ?? [];
  const editarParam = params.get("editar");
  useEffect(() => {
    if (!editarParam || !listado.data) return;
    const elegida = listado.data.find((c) => String(c.id) === editarParam);
    setParams((p) => {
      const nuevo = new URLSearchParams(p);
      nuevo.delete("editar");
      return nuevo;
    }, { replace: true });
    if (elegida && esAdmin) {
      abrirForm(elegida);
      setVolverAFicha(elegida.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se abre una sola vez, cuando llega el listado
  }, [editarParam, listado.data]);

  const manual = usePasosManual(esAdmin, () => {
    categorias.recargar();
    listado.recargar(); // cambian los números
  });
  const nombreProveedorFiltro = proveedorId
    ? (proveedores.data?.find((p) => String(p.id) === proveedorId)?.nombre ?? todas.find((c) => String(c.proveedor_id) === proveedorId)?.proveedor ?? `#${proveedorId}`)
    : null;
  const hayFiltros = estado !== TODOS || proveedorId !== null || texto.trim() !== "";
  const arbol = armarArbol(categorias.data ?? [], buscarCotizaciones(todas, texto), edicion.orden, hayFiltros, manual.empezadas);
  /** Lo que el usuario abrió o cerró manda; si no, con filtros todo abierto y sin filtros sólo lo que está en trámite. */
  const abierta = (clave: string, etapa?: EtapaRama) => abiertas[clave] ?? (hayFiltros || etapa === "tramite");

  return {
    esAdmin,
    hoy,
    arbol,
    manual,
    abierta,
    alternar: (clave: string, abierto: boolean) => setAbiertas((a) => (a[clave] === abierto ? a : { ...a, [clave]: abierto })),
    edicion,
    vencida: (c: Cotizacion) => estaVencida(c, hoy),
    cargando: (listado.loading && !listado.data) || (categorias.loading && !categorias.data),
    errorCarga: listado.data ? (categorias.data ? null : (categorias.error?.message ?? null)) : (listado.error?.message ?? null),
    reintentar: () => {
      listado.recargar();
      categorias.recargar();
    },
    hayFiltros,
    vacio: hayFiltros
      ? "No hay presupuestos con estos filtros."
      : esAdmin
        ? "Todavía no hay categorías: agregalas con «Editar categorías» o en Obra → Presupuesto."
        : "Todavía no hay categorías cargadas.",
    // escanear un presupuesto (PDF o foto): abre el alta completada con lo que se leyó
    puedeEscanear: esAdmin && escaneo.data?.disponible === true,
    escanear: (archivos: File[]) => {
      if (archivos[0]) void escanear(archivos[0]);
    },
    escaneando: accionEscanear.loading,
    errorEscanear: errorArchivoEscaneo ?? accionEscanear.error,
    escaneado: dialogo?.tipo === "form" && dialogo.cotizacion === null ? escaneado : null,
    duplicado: dialogo?.tipo === "duplicado" && dialogo.escaneo.duplicado ? mensajeDuplicado(dialogo.escaneo.duplicado, dialogo.archivo.name) : null,
    verDuplicado: () => {
      if (dialogo?.tipo === "duplicado" && dialogo.escaneo.duplicado) navigate(`/cotizaciones/${dialogo.escaneo.duplicado.id}`);
    },
    cargarIgual: () => {
      if (dialogo?.tipo === "duplicado") abrirAltaEscaneada(dialogo.archivo, dialogo.escaneo);
    },
    guardarLineas,
    setGuardarLineas,
    guardarCuotas,
    setGuardarCuotas,
    proveedorLeidoNuevo,
    // filtros
    estado,
    setEstado,
    texto,
    setTexto,
    nombreProveedorFiltro,
    quitarProveedor: () =>
      setParams((p) => {
        const nuevo = new URLSearchParams(p);
        nuevo.delete("proveedor");
        return nuevo;
      }),
    // comparar, dentro de cada subcategoría
    comparacion: (lista: Cotizacion[]) => compararCotizaciones(lista, hoy),
    abrirFicha: (c: Cotizacion) => navigate(`/cotizaciones/${c.id}`),
    // diálogos
    dialogo,
    cerrar,
    /** Desde una subcategoría del árbol, el alta ya la trae elegida. */
    abrirNuevo: (itemId?: number) => {
      setEscaneado(null);
      abrirForm(null, { ...formCotizacion(undefined, hoy), item_id: itemId === undefined ? "" : String(itemId) });
    },
    abrirEditar: (c: Cotizacion) => abrirForm(c),
    abrirAdjuntos: (c: Cotizacion) => {
      accionSubir.limpiarError();
      setDialogo({ tipo: "adjuntos", cotizacion: c });
    },
    form,
    cambiar,
    errores: mostrarErrores ? errores : SIN_ERRORES,
    elegirProveedor: (id: string) => {
      accionCrearProveedor.limpiarError();
      cambiar("proveedor_id", id);
    },
    quitarProveedorForm: () => cambiar("proveedor_id", ""),
    crearProveedor: (nombre: string) => void crearProveedor(nombre),
    creandoProveedor: accionCrearProveedor.loading,
    errorCrearProveedor: accionCrearProveedor.error,
    nombreProveedorForm: form.proveedor_id
      ? (proveedores.data?.find((p) => String(p.id) === form.proveedor_id)?.nombre ??
        (proveedorCreado && String(proveedorCreado.id) === form.proveedor_id ? proveedorCreado.nombre : null) ??
        (dialogo?.tipo === "form" && dialogo.cotizacion ? dialogo.cotizacion.proveedor : `#${form.proveedor_id}`))
      : null,
    opcionesProveedorForm: opcionesProveedorCotizacion(proveedores.data ?? []),
    opcionesEstadoForm: opcionesEstadoForm(form.estado),
    opcionesSubcategoria: opcionesSubcategoria(categorias.data ?? []),
    /** Editando uno de antes sin subcategoría: su etiqueta, para saber dónde ubicarlo. */
    etiquetaVieja: dialogo?.tipo === "form" && dialogo.cotizacion?.item_id === null ? dialogo.cotizacion.etiqueta : null,
    guardar,
    guardando: accionGuardar.loading || accionSubir.loading || accionCrearProveedor.loading,
    errorGuardar: accionGuardar.error ?? (proveedorLeidoNuevo ? accionCrearProveedor.error : null),
    abrirBorrar: (c: Cotizacion) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", cotizacion: c });
    },
    mensajeBorrado: dialogo?.tipo === "borrar" ? mensajeBorrado(dialogo.cotizacion) : "",
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    // adjuntos
    adjuntos,
    cargandoAdjuntos: conAdjuntos !== null && detalle.loading && detalle.data?.id !== conAdjuntos.id,
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subirAdjunto,
    borrarAdjunto,
    subiendo: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error ?? (conAdjuntos && !detalle.data ? (detalle.error?.message ?? null) : null),
  };
}
